import type { GroupSearchResultItem, SearchResultItemSection, SearchResultItemTask } from '../../types';
import { useState } from 'react';
import { Body, Divider, Button, Icon, Stack, Utility } from '@workramp/ui';
import { useTheme } from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import { I18n } from '~/common/explicit-exports';
import { useDateTimeTextFormatter } from '~/features/temporal';
import { MixPanel } from '~/common';
import { getRedirectUrlForSearchResult, getIconPropsSearchResult, getResultInfo, getSectionName } from '../../utils';
import { SearchResultContentTypes, SectionTypes, LearnerSearchMixpanelEvents } from '../../constants';
import { ResultTitle, IconWrapper, ResultBodyStack, ShowButton } from './LearnerSearchResult.styles';

type AdvancedSearchResultProps = {
  resultItem: GroupSearchResultItem;
  resultTarget?: '_self' | '_blank';
};

type ResultSectionProps = {
  item: SearchResultItemSection | SearchResultItemTask;
  resultTarget?: '_self' | '_blank';
};

const ResultSection = ({ item, resultTarget }: ResultSectionProps): JSX.Element => {
  const theme = useTheme();
  const titleText = item.snippetMatch?.title ?? item.name ?? null;
  const descriptionText = item.snippetMatch?.subtitle ?? item.description ?? null;

  return (
    <ResultBodyStack size={1}>
      <Button
        variant="text-inline"
        href={getRedirectUrlForSearchResult(item)}
        target={resultTarget}
        onPress={() => {
          MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_CLICKS_RESULT);
        }}
      >
        <Utility isExpandable level="xl" maxLines={1} as="p">
          {ReactHtmlParser(titleText)}
        </Utility>
      </Button>
      {descriptionText && (
        <Body maxLines={1} level="sm" color={theme.colors.on[900]}>
          {ReactHtmlParser(descriptionText)}
        </Body>
      )}
      <Body level="sm" color={theme.colors.on[600]}>
        {getSectionName(item)}
      </Body>
    </ResultBodyStack>
  );
};

const getSectionAndTaskOrder = (result: SearchResultItemSection | SearchResultItemTask) => {
  if (result.type === SectionTypes.GUIDE_SECTION) {
    return `${result.guide.ui_order}`;
  }
  if (result.type === SectionTypes.GUIDE_TASK) {
    return `${result.guide.ui_order}-${result.guide.section.ui_order}`;
  }
  return '0';
};

const sortSectionAndTask = (
  a: SearchResultItemSection | SearchResultItemTask,
  b: SearchResultItemSection | SearchResultItemTask,
) => {
  const aOrder = getSectionAndTaskOrder(a);
  const bOrder = getSectionAndTaskOrder(b);
  if (aOrder < bOrder) {
    return -1;
  }
  if (aOrder > bOrder) {
    return 1;
  }
  return 0;
};

export const LearnerSearchResult = ({ resultItem, resultTarget = '_self' }: AdvancedSearchResultProps) => {
  const theme = useTheme();
  const SHOW_MORE = I18n.t('js.learner.search.show_more');
  const SHOW_FEWER = I18n.t('js.learner.search.show_fewer');
  const [showMore, setShowMore] = useState(false);

  const dateFormatter = useDateTimeTextFormatter({
    isLocalTime: true,
    showTime: true,
  });

  const getTitleText = () => {
    if (resultItem.type === 'guide') {
      return resultItem.name;
    }
    return resultItem.snippetMatch?.title ?? resultItem.name ?? null;
  };

  const descriptionText = resultItem.snippetMatch?.subtitle ?? resultItem.description ?? null;

  const resultIcon = () => {
    const iconProps = getIconPropsSearchResult({
      type: resultItem.type,
      subType: resultItem.contentSource,
      colors: theme.colors,
    });

    return <Icon name={iconProps.name} color={iconProps.color} />;
  };

  return (
    <>
      <Stack size={4}>
        <Stack size={1}>
          <ResultTitle>
            <IconWrapper>{resultIcon()}</IconWrapper>
            <Button
              variant="text-inline"
              target={resultTarget}
              onPress={() => {
                MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_CLICKS_RESULT);
                const url = getRedirectUrlForSearchResult(resultItem);
                if (resultItem.type === SearchResultContentTypes.CONTENT_PROVIDER_CONTENT) {
                  window.open(url, '_blank');
                } else {
                  window.location.assign(url);
                }
              }}
            >
              <Utility isExpandable level="xl" maxLines={1} as="p">
                {ReactHtmlParser(getTitleText())}
              </Utility>
            </Button>
          </ResultTitle>
          <ResultBodyStack size={1}>
            {descriptionText && (
              <Body maxLines={1} level="sm" color={theme.colors.on[900]}>
                {ReactHtmlParser(descriptionText)}
              </Body>
            )}
            <Body level="sm" color={theme.colors.on[600]}>
              {getResultInfo(resultItem, dateFormatter)}
            </Body>
          </ResultBodyStack>
        </Stack>
        {(showMore ? resultItem.children : resultItem.children.slice(0, 1)).sort(sortSectionAndTask).map((child) => (
          <ResultSection key={child.id} item={child} resultTarget={resultTarget} />
        ))}
        <Stack size={1}>
          {resultItem.children.length > 1 && (
            <ShowButton variant="text" color="secondary" onPressStart={() => setShowMore(!showMore)}>
              {showMore ? SHOW_FEWER : SHOW_MORE}
            </ShowButton>
          )}
        </Stack>
      </Stack>
      <Divider spacing={4} />
    </>
  );
};
